import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useUsuario } from '@/components/hooks/useUsuario';
import { DLG_BTN_PRIMARY } from '@/components/ui/dialogButtons';

interface LoginDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  children?: React.ReactNode;
  context?: 'login' | 'clientBooking';
  /** Cierra este dialogo y abre el de registro de cliente */
  onRegisterClient?: () => void;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.72 1.22 9.22 3.22l6.9-6.9C35.9 2.34 30.37 0 24 0 14.64 0 6.47 5.37 2.56 13.18l8.02 6.23C12.67 13.17 17.9 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.1 24.5c0-1.64-.15-3.22-.43-4.75H24v9.02h12.42c-.54 2.9-2.18 5.36-4.62 7.02l7.12 5.54C43.5 37.1 46.1 31.3 46.1 24.5z"
    />
    <path
      fill="#FBBC05"
      d="M10.58 28.41a14.5 14.5 0 010-8.82l-8.02-6.23C.9 16.74 0 20.28 0 24c0 3.72.9 7.26 2.56 10.64l8.02-6.23z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.37 0 11.9-2.1 15.88-5.67l-7.12-5.54c-1.97 1.32-4.5 2.1-8.76 2.1-6.1 0-11.33-3.67-13.42-9.04l-8.02 6.23C6.47 42.63 14.64 48 24 48z"
    />
  </svg>
);

const LoginDialog: React.FC<LoginDialogProps> = ({
  open,
  setOpen,
  children,
  context = 'login',
  onRegisterClient,
}) => {
  const { t } = useTranslation();
  const { login, loginWithGoogle, resetPassword } = useUsuario();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const redirectTarget =
      context === 'clientBooking' ? 'clientBooking' : 'login';

    localStorage.setItem('post_login_redirect', redirectTarget);

    setLoading(true);
    const result = await login(email, password, context);
    setLoading(false);

    if (result?.ok) setOpen(false);
  };

  const handleGoogleLogin = async () => {
    const redirectTarget =
      context === 'clientBooking' ? 'clientBooking' : 'login';

    localStorage.setItem('post_login_redirect', redirectTarget);

    if (context === 'clientBooking') {
      localStorage.setItem('clientBooking_returnUrl', window.location.pathname);
    }

    localStorage.setItem('oauth_in_progress', '1');
    setOauthLoading(true);

    await loginWithGoogle();
  };

  const handleResetPassword = async () => {
    setResetLoading(true);
    await resetPassword(email);
    setResetLoading(false);
  };

  return (
    <>
      {oauthLoading && (
        <div className="fixed inset-0 z-[9999] bg-white/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-[#1c1c1e] px-8 py-6 rounded-md shadow-xl flex flex-col items-center gap-4 border border-gray-100 dark:border-white/10">
            <div className="h-12 w-12 rounded-full border-4 border-gray-300 dark:border-white/10 border-t-[#103254] animate-spin" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('loginDialog.redirectingGoogle')}
            </span>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        {children ? (
          <DialogTrigger asChild>{children}</DialogTrigger>
        ) : (
          <DialogTrigger>
            <Button className={`${DLG_BTN_PRIMARY} w-full`}>
              {t('loginDialog.trigger')}
            </Button>
          </DialogTrigger>
        )}

        {/* Nota: se quita [&>button:last-child]:hidden para conservar la X
            por defecto del Dialog (aquí sí hace falta, a diferencia de
            LocationDialog que solo tiene botones Sí/No) */}
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            data-[state=open]:duration-300 data-[state=closed]:duration-200
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[92%] sm:max-w-[480px] sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
            p-6 overflow-y-auto
          "
        >
          <DialogHeader>
            <DialogTitle className="text-[#103254] dark:text-white">
              {t('loginDialog.title')}
            </DialogTitle>
          </DialogHeader>

          <Button
            variant="outline"
            className="w-full flex items-center justify-center gap-3 mt-4 dark:bg-white/10 dark:border-white/20 dark:text-white dark:hover:bg-white/20"
            onClick={handleGoogleLogin}
          >
            <GoogleIcon />
            {t('loginDialog.googleBtn')}
          </Button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-300 dark:bg-white/10" />
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {t('loginDialog.orDivider')}
            </span>
            <div className="flex-1 h-px bg-gray-300 dark:bg-white/10" />
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="dark:text-gray-300">{t('loginDialog.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            <div>
              <Label htmlFor="password" className="dark:text-gray-300">{t('loginDialog.passwordLabel')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            <p
              onClick={handleResetPassword}
              className="text-sm text-[#103254] dark:text-gray-300 hover:underline cursor-pointer text-right"
            >
              {resetLoading
                ? t('loginDialog.sendingReset')
                : t('loginDialog.forgotPassword')}
            </p>

            <Button
              type="submit"
              className={`${DLG_BTN_PRIMARY} w-full`}
              disabled={loading}
            >
              {loading ? t('loginDialog.submitting') : t('loginDialog.submit')}
            </Button>
          </form>

          {onRegisterClient && (
            <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('loginDialog.noAccount', '¿Aún no tienes cuenta?')}{' '}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onRegisterClient();
                }}
                className="font-semibold text-[#288E7F] hover:underline"
              >
                {t('loginDialog.registerClient', 'Regístrate como cliente')}
              </button>
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginDialog;